import { find, get } from 'lodash';
import { formatIsoDateToLongDate } from '@utils/dateHelpers';
import { ATTR_NAME_CARNEGIE, ATTR_NAME_FLUENCY, ATTR_NAME_PRONUNCIATION, DEFAULT_EVALUATION_REPORT_OVERALL_MAX_SCORE, SECTION_ATTRIBUTE_TYPE, } from '@containers/Assessment/assessment.constants';
export const formatExecuteCodeSubscriptionOutput = (output) => ({
    id: output.id,
    stdout: output.std_out,
    stderr: output.std_err,
    cpuTime: output.cpu_time,
    memory: output.memory_used,
    status: output.status,
});
export const formatIvyReportDetails = (rawResponse, includeVisualCues = true, includeScoring = true) => {
    const data = get(rawResponse, 'res_find.data.ref');
    if (!data) {
        throw new Error('Invalid response format: Missing data.res_find.data.ref');
    }
    const sectionConfigAttrs = get(data, 'section_configurations.section_configuration_attributes', []);
    const baseSkills = buildRatings(get(data, 'parameters', []), sectionConfigAttrs, 0);
    const skills = includeScoring
        ? baseSkills
        : baseSkills.map(({ score: _s, maxScore: _m, ...rest }) => rest);
    // Extract overall max score
    const overallAttribute = find(sectionConfigAttrs, (attr) => get(attr, 'type') === SECTION_ATTRIBUTE_TYPE.OVERALL);
    const numericScore = get(data, 'numeric_score');
    const hasValidNumericScore = typeof numericScore === 'number' && numericScore >= 0;
    const configuredMaxScore = get(overallAttribute, 'max_value');
    const maxScore = typeof configuredMaxScore === 'number' && configuredMaxScore > 0
        ? configuredMaxScore
        : hasValidNumericScore
            ? DEFAULT_EVALUATION_REPORT_OVERALL_MAX_SCORE
            : undefined;
    return {
        id: get(data, 'instance_id', 0),
        assessment_id: get(data, 'test_id', 0),
        assessment_type: get(data, 'assessment_section_title'),
        assessment_name: get(data, 'assessment_name'),
        candidate_id: get(data, 'candidate_id'),
        email: get(data, 'email'),
        name: get(data, 'name', ''),
        parameters: skills,
        report_generated_on: formatIsoDateToLongDate(get(data, 'report_generation_time')),
        transcript: get(data, 'transcript'),
        visual_cues: includeVisualCues ? data?.visual_cues : undefined,
        overall_score: includeScoring && hasValidNumericScore ? numericScore : undefined,
        max_score: includeScoring ? maxScore : undefined,
    };
};
const isScoreWithinRange = (score, min, max) => {
    const lowerBound = typeof min === 'number' ? min : -Infinity;
    const upperBound = typeof max === 'number' ? max : Infinity;
    return score >= lowerBound && score <= upperBound;
};
const getOrdinalForScore = (attribute, score) => {
    if (!attribute || typeof score !== 'number' || !Array.isArray(attribute.ordinals))
        return null;
    const match = attribute.ordinals.find((ordinal) => isScoreWithinRange(score, ordinal?.min_value, ordinal?.max_value));
    return match ?? null;
};
const getOrdinalLabelByValue = (attribute, value) => {
    if (!attribute || !value || !Array.isArray(attribute.ordinals))
        return null;
    const match = attribute.ordinals.find((ordinal) => ordinal?.value === value);
    return match?.label ?? null;
};
const findSubValueOrdinalAttr = (sectionConfigAttrs, name) => sectionConfigAttrs.find((attr) => attr.type === SECTION_ATTRIBUTE_TYPE.SUB_VALUE && attr.attribute_type === 'ORDINAL' && attr.name === name);
const buildRatings = (parameters, sectionConfigAttributes, defaultMaxValue = 0, useOrdinalRemarksOnly = false) => parameters.reduce((acc, param) => {
    const nameValue = get(param, 'name');
    if (typeof nameValue !== 'string' || nameValue.trim().length === 0)
        return acc;
    const attribute = sectionConfigAttributes.find((attr) => attr.type === SECTION_ATTRIBUTE_TYPE.SUB_VALUE && attr.name === nameValue);
    const rawScore = get(param, 'numeric_score');
    const numericScore = typeof rawScore === 'number' ? rawScore : 0;
    const maxValue = typeof attribute?.max_value === 'number' ? attribute.max_value : defaultMaxValue;
    const rawRemarks = get(param, 'remarks');
    const ordinal = getOrdinalForScore(attribute, numericScore);
    const overrideOrdinals = [ATTR_NAME_FLUENCY, ATTR_NAME_PRONUNCIATION];
    let remarks;
    if (useOrdinalRemarksOnly) {
        remarks = ordinal?.label ?? undefined;
    }
    else if (overrideOrdinals.includes(nameValue) && ordinal?.label) {
        remarks = ordinal.label;
    }
    else if (typeof rawRemarks === 'string') {
        remarks = rawRemarks;
    }
    acc.push({
        name: nameValue,
        score: numericScore,
        maxScore: maxValue,
        remarks,
        sectionScoreLabel: ordinal?.value ?? null,
        sectionScoreDescription: ordinal?.label ?? null,
    });
    return acc;
}, []);
const extractRef = (rawResponse) => {
    const ref = rawResponse.res_find?.data?.ref;
    if (!ref)
        throw new Error('Invalid response format: Missing res_find.data.ref');
    return ref;
};
const buildBaseReportFromRef = (ref) => ({
    reportId: ref.instance_id ?? null,
    assessmentId: ref.test_id ?? null,
    assessmentName: ref.assessment_name ?? null,
    assessmentType: ref.assessment_section_title ?? null,
    candidateId: ref.candidate_id ?? null,
    candidateName: ref.name ?? null,
    candidateExternalId: ref.external_id ?? null,
    completionDate: formatIsoDateToLongDate(ref.report_generation_time),
});
const buildSpeakingSectionFromRef = (ref) => {
    const sectionConfigAttrs = ref.section_configurations?.section_configuration_attributes ?? [];
    const overallAttribute = sectionConfigAttrs.find((attr) => attr.type === SECTION_ATTRIBUTE_TYPE.OVERALL);
    const fluencyAttribute = findSubValueOrdinalAttr(sectionConfigAttrs, ATTR_NAME_FLUENCY);
    const pronunciationAttribute = findSubValueOrdinalAttr(sectionConfigAttrs, ATTR_NAME_PRONUNCIATION);
    const carnegieAttribute = findSubValueOrdinalAttr(sectionConfigAttrs, ATTR_NAME_CARNEGIE);
    const allRatings = buildRatings(ref.parameters ?? [], sectionConfigAttrs, 5);
    const ratings = allRatings.filter((r) => r.name !== ATTR_NAME_CARNEGIE);
    const fluencyScore = allRatings.find((r) => r.name === ATTR_NAME_FLUENCY)?.score;
    const pronunciationScore = allRatings.find((r) => r.name === ATTR_NAME_PRONUNCIATION)?.score;
    const numericScore = ref.numeric_score ?? null;
    const hasScore = numericScore !== null;
    const overallScoreValue = hasScore ? Number(numericScore) : null;
    const overallMax = typeof overallAttribute?.max_value === 'number'
        ? overallAttribute.max_value
        : DEFAULT_EVALUATION_REPORT_OVERALL_MAX_SCORE;
    if (ratings.length === 0 && !hasScore)
        return undefined;
    const scoreValues = [fluencyScore, pronunciationScore].filter((v) => typeof v === 'number');
    const averagedNormalizedScore = scoreValues.length > 0 ? scoreValues.reduce((acc, v) => acc + v, 0) / scoreValues.length : null;
    const ordinalSource = fluencyAttribute ?? pronunciationAttribute;
    const averagedOrdinal = ordinalSource && typeof averagedNormalizedScore === 'number'
        ? getOrdinalForScore(ordinalSource, averagedNormalizedScore)
        : null;
    const overallOrdinal = getOrdinalForScore(overallAttribute, overallScoreValue);
    const carnegieSummary = getOrdinalLabelByValue(carnegieAttribute, averagedOrdinal?.value);
    return {
        score: overallScoreValue,
        maxScore: overallMax,
        cefrLevel: overallOrdinal?.value ?? null,
        summary: carnegieSummary ?? null,
        overallSummary: overallOrdinal?.label ?? null,
        ratings,
    };
};
const buildWritingSectionFromRef = (ref) => {
    const sectionConfigAttrs = ref.section_configurations?.section_configuration_attributes ?? [];
    const allRatings = buildRatings(ref.parameters ?? [], sectionConfigAttrs, 5, true);
    const ratings = allRatings.filter((r) => r.name !== ATTR_NAME_CARNEGIE);
    const overallAttribute = sectionConfigAttrs.find((attr) => attr.type === SECTION_ATTRIBUTE_TYPE.OVERALL);
    const numericScore = ref.numeric_score ?? null;
    const hasScore = numericScore !== null;
    const overallScoreValue = hasScore ? Number(numericScore) : null;
    const overallMax = typeof overallAttribute?.max_value === 'number'
        ? overallAttribute.max_value
        : DEFAULT_EVALUATION_REPORT_OVERALL_MAX_SCORE;
    if (ratings.length === 0 && !hasScore)
        return null;
    const overallOrdinal = getOrdinalForScore(overallAttribute, overallScoreValue);
    return {
        score: overallScoreValue,
        maxScore: overallMax,
        cefrLevel: overallOrdinal?.value ?? null,
        summary: overallOrdinal?.label ?? null,
        overallSummary: overallOrdinal?.label ?? null,
        ratings,
    };
};
export const formatLanguageEvaluationReport = (rawResponse) => {
    const ref = extractRef(rawResponse);
    return {
        ...buildBaseReportFromRef(ref),
        speaking: buildSpeakingSectionFromRef(ref),
    };
};
export const formatWritingEvaluationReport = (rawResponse) => {
    const ref = extractRef(rawResponse);
    const writing = buildWritingSectionFromRef(ref);
    return {
        ...buildBaseReportFromRef(ref),
        writing: writing ?? undefined,
    };
};
export const parseResourceTypeIdFromParams = (searchParams) => {
    const value = Number(searchParams.get('resource_type_id'));
    return Number.isFinite(value) && value > 0 ? value : null;
};
export const parseResourceFromParams = (searchParams) => {
    const value = Number(searchParams.get('resource_id'));
    return Number.isFinite(value) && value > 0 ? value : null;
};
const SECTION_TYPE_MAP = {
    ASYNC: 'speaking',
    LONG_FORM: 'writing',
};
export const normalizeCombinedReportResponse = (raw) => {
    const orderedTypes = [];
    const refs = {};
    for (const section of raw.sections) {
        const langType = SECTION_TYPE_MAP[section.section_type] ?? null;
        if (!langType || refs[langType])
            continue;
        refs[langType] = {
            instance_id: section.instance_id,
            test_id: section.test_id,
            assessment_name: section.assessment_name,
            assessment_section_title: section.assessment_section_title,
            candidate_id: section.candidate_id,
            name: section.name,
            email: section.email,
            external_id: section.external_id,
            report_generation_time: section.report_generated_at,
            numeric_score: section.numeric_score,
            parameters: section.parameters,
            section_configurations: section.section_configurations,
        };
        orderedTypes.push(langType);
    }
    const firstRef = refs.speaking ?? refs.writing;
    if (!firstRef)
        throw new Error('At least one supported section is required');
    const speakingSection = refs.speaking ? buildSpeakingSectionFromRef(refs.speaking) : undefined;
    const writingSection = refs.writing ? buildWritingSectionFromRef(refs.writing) : null;
    const orderedSections = orderedTypes
        .map((type) => {
        if (type === 'speaking' && speakingSection)
            return { type, section: speakingSection };
        if (type === 'writing' && writingSection)
            return { type, section: writingSection };
        return null;
    })
        .filter((value) => Boolean(value));
    return {
        ...buildBaseReportFromRef(firstRef),
        speaking: speakingSection,
        writing: writingSection ?? undefined,
        sections: orderedSections,
    };
};
